import axios from "axios";
import TokenService from "../utils/token-service";
import { API_URL } from "../utils/baseUrl";
import { Loader, Toaster, getUserTypeID } from "../utils/common";
import {
  ensureAuthCacheSynced,
  getAuthUserId,
} from "../utils/authSession";

function responseHandler(response) {
  if (response.status === 200 && response.data.status === 1) {
    Toaster.success(response.data.message);
    return response.data.data;
  } else if (response.status === 200 && response.data.status === 0) {
    Toaster.warning(response.data.message);
    return false;
  } else {
    Toaster.error("Something went wrong, please try again");
  }
}

const instance = axios.create({
  baseURL: API_URL,
  withCredentials: true, // send/receive auth cookies (trusted browser, session)
  // headers: {
  //   "Content-Type": "application/json",
  // },
});

// Keep the loader visible until every in-flight request finishes.
// Opening a folder fires several calls; hiding on the first response
// showed the page before getUserDocs returned the full details.
let activeApiRequests = 0;

function beginApiLoader() {
  activeApiRequests += 1;
  Loader(true);
}

function endApiLoader() {
  activeApiRequests = Math.max(0, activeApiRequests - 1);
  if (activeApiRequests === 0) Loader(false);
}

instance.interceptors.request.use(
  (config) => {
    beginApiLoader();
    ensureAuthCacheSynced();
    const token = TokenService.getLocalAccessToken();
    if (token) {
      // config.headers["Authorization"] = 'Bearer ' + token;  // for Spring Boot back-end
      config.headers["x-access-token"] = token; // for Node.js Express back-end
      config.headers["user-type"] = getUserTypeID();
      config.headers["user-id"] =
        getAuthUserId() || localStorage.getItem("user_id");
    }
    return config;
  },
  (error) => {
    endApiLoader();

    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (res) => {
    endApiLoader();
    return res;
  },
  async (err) => {
    endApiLoader();
    const originalConfig = err.config;
    if (originalConfig.url !== "/auth/login" && err.response) {
      // Access Token was expired
      if (err.response.status === 401 && !originalConfig._retry) {
        originalConfig._retry = true;

        try {
          const rs = await instance.post("/auth/refreshtoken", {
            requestToken: TokenService.getLocalRefreshToken(),
          });

          const { accessToken, user_type, user_id } = rs.data || {};
          TokenService.updateLocalAccessToken(accessToken);
          if (user_type || user_id) {
            ensureAuthCacheSynced();
          }

          return instance({
            ...originalConfig,
            headers: {
              ...originalConfig.headers,
              "x-access-token": TokenService.getLocalAccessToken(),
              "user-type": getUserTypeID(),
              "user-id":
                getAuthUserId() || localStorage.getItem("user_id"),
            },
            sent: true,
          });
        } catch (_error) {
          // navigate("/login");
          return Promise.reject(_error);
        }
      } else if (err.response.status === 403) {
        if (originalConfig?.skipForbiddenRedirect) {
          return Promise.reject(err);
        }
        // Never hard-redirect to /forbidden after login — that broke the login flow
        // (e.g. getNotification 403). Show a warning and let the page handle it.
        const msg =
          (typeof err.response.data === "string"
            ? err.response.data
            : err.response.data?.message) ||
          err.response?.message ||
          "Access forbidden";
        // Ignore JWT "No token" noise during the brief post-login race.
        if (!/no token provided/i.test(String(msg))) {
          Toaster.warning(msg);
        }
        return Promise.reject(err);
      }
    } else if (err.code === "ERR_NETWORK") {
      Toaster.warning("Check your connection and try again");
    }
    return Promise.reject(err);
  }
);

export default instance;
